<template>
	<div class="h-100" ref="preview_parent">
		<component v-if="!delayForChildRender && entityTypeSupported" :is="localData.component" @rendered="childRendered" @buttons-changed="buttonsChanged" @title-changed="titleChanged" :options="componentOptions"></component>
	</div>
</template>

<script>
	import _ from 'lodash'
	import { defineAsyncComponent } from 'vue'
	// import Payment from '~/views/treasury/payment.vue'
	// import Receive from '~/views/treasury/receive.vue'
	// import Payment_Hybrid from '~/views/treasury/payment-hybrid.vue'
	// import Receive_Hybrid from '~/views/treasury/receive-hybrid.vue'
	// import Transfer from '~/views/treasury/transfer.vue'
	// import Factor from '~/views/sale/factors/factor-modify.vue'
	// import BuyOrder from '~/views/sale/products/buy-order/modify'
	// import ProductRequest from '~/views/sale/products/request/modify'
	// import WarehouseReceiptHawala from '~/views/sale/warehouse/receipt-hawala-modify'
	import includes from 'lodash/includes'

	export default {
		modal: {
			size: 'lg',
			stateUID: 'preview-container',
			helpTag: (e) => {
				if (!e.options?.entityType) return

				if (e.options?.entityType.endsWith('Hybrid')) {
					return e.options?.entityType === 'Payment_Hybrid' ? 'PaymentHybrid' : 'ReceiveHybrid'
				}

				else if (e.options?.entityType.includes('AssetAcquisitionHeader')) {
					return e.options?.entityType.includes('Opening') ? 'AssetsAcquisitionOpening' : 'AssetsAcquisition'
				}

				else if (e.options?.entityType.startsWith('WarehouseReceiptHawala')) {
					const parts = e.options?.entityType.split('_')
					const type = parts[1]
					const group = parts[2]

					if (type === 'receipt') {
						return group ? `WarehouseReceipt${group}` : ''
					} else if (type === 'hawala') {
						return group ? `WarehouseHawala${group}` : ''
					}
				}

				if (e.options?.entityType.startsWith('Factor_')) {
					const type = e.options?.entityType.split('_')[1]
					switch (type) {
						case 'Proforma': return 'FactorProforma'
						case 'Sale': return 'SaleFactor'
						case 'SaleReturn': return 'SaleReturnFactor'
						case 'Buy': return 'BuyFactor'
						case 'BuyReturn': return 'BuyReturnFactor'
					}
				}

				if (e.options?.entityType.startsWith('Transfer_')) {
					return 'Transfer'
				}

				switch (e.options?.entityType) {
					case 'DocumentPreview': return 'DocumentPreview'
					case 'BuyOrder': return 'BuyOrder'
					case 'ProductRequest': return 'ProductRequest'
					case 'HawalaDocumentForm': return 'HawalaDocumentForm'
					case 'CommissionAnnouncement': return 'CommissionAnnouncement'
					default: return
				}
			}
		},
		components: {
			Payment: defineAsyncComponent(() => import('~/views/treasury/payment.vue')),
			Receive: defineAsyncComponent(() => import('~/views/treasury/receive.vue')),
			Factor: defineAsyncComponent(() => import('~/views/sale/factors/factor-modify.vue')),
			Transfer: defineAsyncComponent(() => import('~/views/treasury/transfer.vue')),
			Payment_Hybrid: defineAsyncComponent(() => import('~/views/treasury/payment-hybrid.vue')),
			Receive_Hybrid: defineAsyncComponent(() => import('~/views/treasury/receive-hybrid.vue')),
			DocumentPreview: defineAsyncComponent(() => import('~/views/documents/document-preview.vue')),
			BuyOrder: defineAsyncComponent(() => import('~/views/sale/products/buy-order/modify')),
			ProductRequest: defineAsyncComponent(() => import('~/views/sale/products/request/modify')),
			WarehouseReceiptHawala: defineAsyncComponent(() => import('~/views/sale/warehouse/receipt-hawala-modify')),
			AssetAcquisition: defineAsyncComponent(() => import('~/views/assets/assets-acquisition')),
			HawalaDocumentForm: defineAsyncComponent(() => import('~/views/sale/warehouse/hawala-document-form-modify')),
			CommissionAnnouncement: defineAsyncComponent(() => import('~/views/sale/commission/commission-announcement-modify')),
		},
		props: {
			options: {},
			tabindex: {}
		},
		emits: ['title-changed', 'buttons-changed'],
		data() {
			return {
				settings: {},
				componentOptions: {},
				localData: {},
				wait: null,
				delayForChildRender: true,
				entityTypeSupported: true
			}
		},
		watch: {
			options: {
				immediate: true,
				deep: true,
				handler(val, oldVal) {
					this.settings = _.merge(this.settings, val)
				}
			}
		},
		mounted() {
			this.wait = this.$wait(this.$refs.preview_parent)
			this.componentOptions = this.settings
			this.decideSuitableComponent()
			this.prepareComponentOptions()
			this.defer(() => {
				this.delayForChildRender = false
			}, 1000)
		},
		methods: {
			childRendered() {
				this.wait.end()
			},
			buttonsChanged(buttons) {
				this.$emit('buttons-changed', buttons)
			},
			titleChanged(title) {
				this.$emit('title-changed', title)
			},
			decideSuitableComponent() {
				if (this.settings.entityType.endsWith('Hybrid')) // eg: Payment_Hybrid - Receive_Hybrid
					this.localData.component = this.settings.entityType

				else if (includes(this.settings.entityType, 'AssetAcquisitionHeader'))
					this.localData.component = 'AssetAcquisition'

				else if (includes(this.settings.entityType, '_')) {
					const splits = this.settings.entityType.split('_')
					this.localData.type = splits[1] // eg: Cash - Cheque - CashCash - Receipt ...
					this.localData.group = splits.length > 1 ? splits[2] : this.settings.group // eg: BuyReceipt - SaleHawla ...
					this.localData.code = this.settings.code // eg: BuyReceipt - SaleHawla ...
					this.localData.component = this.settings.entityType.split('_')[0] // eg: Payment - Receive - Transfer - WarehouseReceiptHawala ...
				}

				else if (this.settings.entityType === 'BuyOrder')
					this.localData.component = 'BuyOrder'

				else if (this.settings.entityType === 'DocumentPreview')
					this.localData.component = 'DocumentPreview'

				else if (this.settings.entityType === 'ProductRequest')
					this.localData.component = 'ProductRequest'

				else if (this.settings.entityType === 'HawalaDocumentForm')
					this.localData.component = 'HawalaDocumentForm'

				else if (this.settings.entityType === 'CommissionAnnouncement')
					this.localData.component = 'CommissionAnnouncement'

				else {
					this.logger.error('EntityType Not Supported!')
					this.entityTypeSupported = false
					this.childRendered()
				}
			},
			prepareComponentOptions() {
				this.componentOptions.id = this.settings.entityId
				if (!this.componentOptions.mode)
					this.componentOptions.mode = this.FORM_MODES.READONLY
				switch (this.settings.entityType) {
					case 'DocumentPreview':
					case 'BuyOrder':
					case 'ProductRequest':
					case 'Payment_Hybrid':
					case 'Recieve_Hybrid':
					case 'Cheque':
					case 'AssetAcquisitionHeader_Default':
					case 'HawalaDocumentForm':
					case 'CommissionAnnouncement':
						break
					case `Factor_${this.localData.type}`:
						this.componentOptions.type = this.localData.type
						break
					case `Transfer_${this.localData.type}`:
						this.componentOptions.isPreview = true
						break
					case `WarehouseReceiptHawala_${this.localData.type}`:
					case `WarehouseReceiptHawala_${this.localData.type}_${this.localData.group}`:
						this.componentOptions.type = this.localData.type
						this.componentOptions.group = this.localData.group
						break
					case 'AssetAcquisitionHeader_Opening':
						this.componentOptions.type = 'Opening'
					default: // all single payments/receives and transfers
						this.componentOptions.method = this.localData.type
						break
				}
			}
		}
	}
</script>

<style></style>
